import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { replaceState } from 'history-throttled'
import { useLocation } from 'react-router-dom'

import { ChainId, getChainNameFromChainId, isTestnet } from '@src/configs/chainInfo'
import { getDefaultTokens } from '@src/configs/defaults'
import { V2_API_URL } from '@src/configs/env'
import * as PATH from '@src/configs/paths'
import { DEFAULT_BUY_TOKEN, DEFAULT_SELL_TOKEN } from '@src/constants/defaultTokens'
import { QUOTE_REFRESH_BUFFER, QUOTE_REFRESH_INTERVAL } from '@src/constants/exchange'
import { LOCAL_STORAGE_BUY_TOKEN_KEY, LOCAL_STORAGE_SELL_TOKEN_KEY } from '@src/constants/localstorage'
import { LiquiditySource } from '@src/enums'
import { useFetchServerlessGasEstimates } from '@src/hooks/useFetchServerlessGasEstimates'
import { useQuote } from '@src/hooks/useQuote'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import { useLiquidityCheck } from '@src/pages/Exchange/hooks/orders/useLiquidityCheck'
import { MilesRequirementStatus, useMilesRequirement } from '@src/pages/Exchange/hooks/useMilesRequirement'
import { TransactionCapStatus, useTransactionCap } from '@src/pages/Exchange/hooks/useTransactionCap'
import { IExchangeData } from '@src/types/exchange'
import { IFeatherFormattedQuoteResponse } from '@src/types/feather'
import { IQuoteParams, IQuoteResponse } from '@src/types/order'
import { ISavedUrlParams } from '@src/types/savedUrlParams'
import { Address } from '@src/types/token'
import { isFeatherChain } from '@src/utils/chainUtils'
import { isFeatureEnabled } from '@src/utils/environment'
import { isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'
import { createContextLogger } from '@src/utils/logger'
import { useApplicationStore, useTokensStore } from '@src/zustand_stores'

// Create the context before any potentially circular imports
// Define the type for the context data
interface ExchangeDataContextType {
    sellData: IExchangeData
    buyData: IExchangeData
    destinationAddress: Address | undefined
    quoteResponse: IQuoteResponse
    isQuoteLoading: boolean
    refetchQuote: () => Promise<any>
    invalidateQuoteAndRefetch: (newParams: IQuoteParams) => Promise<any>
    forceCancelQuote: () => Promise<any>
    mainWalletAddress: Address | undefined
    updateSellData: (updates: Partial<IExchangeData>) => void
    updateBuyData: (updates: Partial<IExchangeData>) => void
    updateBothData: (sellUpdates: Partial<IExchangeData>, buyUpdates: Partial<IExchangeData>) => void
    setDestinationAddress: (address: Address | undefined) => void
    transactionCapStatus: TransactionCapStatus | null
    isLoadingTransactionCap: boolean
    transactionCapError: string | null
    milesStatus: MilesRequirementStatus
    isJupiterRouteModalOpen: boolean
    openJupiterRouteModal: () => void
    closeJupiterRouteModal: () => void
    isFeatherOrder: boolean
    liquidityAvailable: boolean
    isCheckingLiquidity: boolean
    liquidityError: string | null
    featherQuote: IFeatherFormattedQuoteResponse | null
    featherQuoteError: string | null
}

// Create the context with undefined as default value
const ExchangeDataContext = createContext<ExchangeDataContextType | undefined>(undefined)

// Create logger early
const logger = createContextLogger('ExchangeDataContext')

// Define default data with fallbacks to prevent crashes
const DEFAULT_SELL_DATA: IExchangeData = {
    token: DEFAULT_SELL_TOKEN, // Fallback to static default
    chainId: DEFAULT_SELL_TOKEN.chainId,
    value: BigInt(0),
    liquiditySource: LiquiditySource.unavailable,
}

const DEFAULT_BUY_DATA: IExchangeData = {
    token: DEFAULT_BUY_TOKEN, // Fallback to static default
    chainId: DEFAULT_BUY_TOKEN.chainId,
    value: BigInt(0),
    liquiditySource: LiquiditySource.unavailable,
}

/**
 * Helper to load a token from localStorage and match it against the tokens store.
 */
function loadTokenFromLocalStorage(key: string, allTokens: IExchangeData['token'][]): IExchangeData['token'] | null {
    const saved = localStorage.getItem(key)
    logger.info(`[buyselldefault] Attempting to load token for key ${key}:`, saved)
    if (!saved) return null
    try {
        const tokenData = JSON.parse(saved)

        // Skip matching if no tokens are loaded yet
        if (!allTokens || allTokens.length === 0) {
            logger.warn(`[buyselldefault] Cannot match token from localStorage - token list is empty`)
            return null
        }

        logger.info(`[buyselldefault] Parsed token data:`, {
            symbol: tokenData.symbol,
            chainId: tokenData.chainId,
            address: tokenData.address,
            availableTokensCount: allTokens.length,
        })

        // Attempt to find a matching token in the current token list
        const found = allTokens.find(
            t => t.chainId === tokenData.chainId && t.address.toLowerCase() === tokenData.address.toLowerCase()
        )

        if (found) {
            logger.info(`[buyselldefault] Found matching token in tokens list:`, found.symbol)
            return found
        } else {
            // If no exact match, try to find a token with the same symbol and chainId
            const symbolMatch = allTokens.find(t => t.chainId === tokenData.chainId && t.symbol === tokenData.symbol)

            if (symbolMatch) {
                logger.info(`[buyselldefault] Found token match by symbol and chainId:`, symbolMatch.symbol)
                return symbolMatch
            }

            logger.warn(`[buyselldefault] No match found for token in localStorage:`, {
                symbol: tokenData.symbol,
                chainId: tokenData.chainId,
                address: tokenData.address,
            })
            return null
        }
    } catch (e) {
        logger.error(`[buyselldefault] Error parsing localStorage token:`, e)
        return null
    }
}

// Utility function to get URL parameters
function getURLParameters(): ISavedUrlParams {
    const queryString = window.location.search.substring(1)
    const parameterPairs = queryString.split('&')
    const parameters: ISavedUrlParams = {
        sellChain: '',
        buyChain: '',
        sellToken: '',
        buyToken: '',
    }

    for (const pair of parameterPairs) {
        const [key, value] = pair.split('=').map(decodeURIComponent)
        if (key && key in parameters) {
            parameters[key as keyof ISavedUrlParams] = value || ''
        }
    }

    return parameters as ISavedUrlParams
}

function getUpdatedURLString(obj: any): string {
    const parameters = getURLParameters()

    Object.keys(obj).forEach(key => {
        parameters[key as keyof ISavedUrlParams] = obj[key]
    })

    const queryStringComponents: string[] = []
    for (const key in parameters) {
        if (key && parameters[key as keyof ISavedUrlParams]) {
            queryStringComponents.push(
                `${encodeURIComponent(key)}=${encodeURIComponent(parameters[key as keyof ISavedUrlParams])}`
            )
        }
    }

    const newQueryString = queryStringComponents.join('&')
    return '?' + newQueryString
}

export const useExchangeDataContext = (): ExchangeDataContextType => {
    const context = useContext(ExchangeDataContext)
    if (!context) {
        throw new Error('useExchangeDataContext must be used within an ExchangeDataProvider')
    }
    return context
}

interface ExchangeDataProviderProps {
    children: React.ReactElement
}

export const ExchangeDataProvider: React.FC<ExchangeDataProviderProps> = ({ children }) => {
    const location = useLocation()
    const { isQuoteEnabled, setIsQuoteEnabled } = useExchangeState()
    const { tokens, isLoading: tokensLoading } = useTokensStore()

    // Fetch gas estimates from the Gas Estimation Worker
    const { data: gasEstimatesData } = useFetchServerlessGasEstimates()

    const walletSelectedSellToken = useApplicationStore(state => state.walletSelectedSellToken)
    const setWalletButtonDisplayChainId = useApplicationStore(state => state.setWalletButtonDisplayChainId)

    const [sellData, setSellData] = useState<IExchangeData>(DEFAULT_SELL_DATA)
    const [buyData, setBuyData] = useState<IExchangeData>(DEFAULT_BUY_DATA)

    // Track whether tokens were explicitly selected by the user
    const [userSelectedSellToken, setUserSelectedSellToken] = useState<boolean>(false)
    const [userSelectedBuyToken, setUserSelectedBuyToken] = useState<boolean>(false)

    const [destinationAddress, setDestinationAddress] = useState<Address | undefined>(undefined)
    const [isReadyForQuote, setIsReadyForQuote] = useState(false)

    const { tronWalletAddress, solanaWalletAddress, evmWalletAddress } = useWalletConnections()

    // Determine main wallet address based on chain
    const mainWalletAddress = useMemo(() => {
        return sellData.chainId === ChainId.Solana
            ? solanaWalletAddress
            : ((sellData.chainId === ChainId.Tron ? tronWalletAddress : evmWalletAddress) as Address)
    }, [evmWalletAddress, sellData.chainId, solanaWalletAddress, tronWalletAddress])

    // Check if the buy chain is a Feather chain
    const isFeatherOrder = useMemo(() => isFeatherChain(buyData.chainId), [buyData.chainId])

    // Check if we're running in testnet mode
    const isTestnet = isFeatureEnabled('USE_TESTNETS_ONLY')

    // Check if liquidity is available for this token pair
    const {
        data: liquidityData,
        isLoading: isCheckingLiquidity,
        error: liquidityCheckError,
    } = useLiquidityCheck(sellData.chainId, sellData.token?.address, buyData.chainId, buyData.token?.address, {
        // Only check liquidity when we have all the required data and it's not a Feather order
        enabled: Boolean(
            sellData.token &&
                buyData.token &&
                sellData.token.address && // Ensure address exists
                buyData.token.address && // Ensure address exists
                !isWrapOnly(sellData, buyData) &&
                !isUnWrapOnly(sellData, buyData) &&
                !isFeatherOrder && // Skip liquidity check for Feather orders
                !isTestnet // Skip liquidity check for testnet environments
        ),
    })

    // Determine if liquidity is available
    const liquidityAvailable = useMemo(() => {
        // For Feather orders with a valid quote ID, always consider liquidity as available
        if (isFeatherOrder && sellData.featherQuoteId) {
            logger.debug('Feather order with valid quote ID, assuming liquidity is available')
            return true
        }

        // If we're not using V2 API or checking liquidity, assume it's available
        if (!isFeatureEnabled('V2_MIGRATION') || !V2_API_URL) {
            logger.debug('V2 API not enabled, assuming liquidity is available')
            return true
        }

        // If we don't have liquidity data yet, don't make a determination
        if (!liquidityData && isCheckingLiquidity) {
            logger.debug('Still checking liquidity, no data yet')
            return true
        }

        // If we have liquidity data, use it
        if (liquidityData) {
            logger.debug('Liquidity check result:', {
                supported: liquidityData.supported,
                message: liquidityData.message,
            })
            return liquidityData.supported
        }

        // Default to available if we can't determine
        return true
    }, [liquidityData, isCheckingLiquidity, isFeatherOrder, sellData.featherQuoteId])

    // Format liquidity error message if any
    const liquidityError = useMemo(() => {
        // For Feather orders with a valid quote, don't show liquidity errors
        if (isFeatherOrder && sellData.featherQuoteId) {
            return null
        }

        if (liquidityCheckError) {
            const errorMsg =
                liquidityCheckError instanceof Error ? liquidityCheckError.message : String(liquidityCheckError)
            logger.error('Liquidity check error:', { error: errorMsg })
            return errorMsg
        }

        if (liquidityData && !liquidityData.supported) {
            const errorMsg = liquidityData.message || 'Liquidity not available for this pair'
            logger.debug('Liquidity unavailable message:', { message: errorMsg })
            return errorMsg
        }

        return null
    }, [liquidityData, liquidityCheckError, isFeatherOrder, sellData.featherQuoteId])

    // Define quote parameters for the unified hook
    const quoteParams = useMemo(() => {
        if (!mainWalletAddress || !sellData.token || !buyData.token) {
            return undefined
        }

        return {
            walletAddress: mainWalletAddress.toString() || '',
            srcChain: getChainNameFromChainId(sellData.chainId),
            dstChain: getChainNameFromChainId(buyData.chainId),
            srcAssetAddress: sellData.token.address,
            dstAssetAddress: buyData.token.address,
            srcAmount: sellData.value,
            targetAddress: destinationAddress ?? '',
        }
    }, [
        mainWalletAddress,
        sellData.chainId,
        sellData.token,
        sellData.value,
        buyData.chainId,
        buyData.token,
        destinationAddress,
    ])

    // Use the new unified quote hook
    const {
        data: quoteResponse,
        isLoading: isQuoteLoading,
        refetch: refetchQuote,
        forceCancel: forceCancelQuote,
        invalidateQuoteAndRefetch,
        featherQuote,
        featherQuoteError,
    } = useQuote(
        quoteParams,
        isQuoteEnabled &&
            !isWrapOnly(sellData, buyData) &&
            !isUnWrapOnly(sellData, buyData) &&
            liquidityAvailable &&
            !isCheckingLiquidity,
        {
            // Feather-specific callbacks
            onOrderCreated: orderData => {
                // Handle Feather order created event
                logger.info('Feather order created:', orderData)
            },
            onOrderStatusChanged: orderStatus => {
                // Handle Feather order status changed event
                logger.info('Feather order status changed:', orderStatus)
            },
        },
        {
            refetchInterval: QUOTE_REFRESH_INTERVAL,
            staleTime: QUOTE_REFRESH_INTERVAL - QUOTE_REFRESH_BUFFER,
            enabled:
                isReadyForQuote &&
                !isWrapOnly(sellData, buyData) &&
                !isUnWrapOnly(sellData, buyData) &&
                Boolean(sellData.value && sellData.token && buyData.token) &&
                !isCheckingLiquidity && // Don't fetch quotes while checking liquidity
                liquidityAvailable, // Only fetch quotes if liquidity is available
        },
        // Pass the token data
        sellData.token,
        buyData.token
    )

    // Update featherQuoteId when a new Feather quote is received
    useEffect(() => {
        if (isFeatherOrder && featherQuote && featherQuote.quoteId) {
            setSellData(prev => ({
                ...prev,
                featherQuoteId: featherQuote.quoteId,
                isFeatherOrder: true,
            }))

            // Also update the buy amount from the Feather quote if available
            if (featherQuote.buyAmount) {
                try {
                    const buyAmountBigInt = BigInt(featherQuote.buyAmount)
                    setBuyData(prev => ({
                        ...prev,
                        value: buyAmountBigInt,
                    }))
                } catch (error) {
                    logger.error('Failed to parse Feather quote buy amount', error)
                }
            }
        }
    }, [isFeatherOrder, featherQuote])

    const { transactionCapStatus, isLoadingTransactionCap, transactionCapError } = useTransactionCap({
        buyData,
        isQuoteLoading,
        mainWalletAddress,
        sellData,
    })

    const milesStatus = useMilesRequirement({
        buyData,
        sellData,
        mainWalletAddress,
    })

    // Effect to handle liquidity check results and update buy data value
    useEffect(() => {
        // If checking liquidity, don't modify the buy data yet
        if (isCheckingLiquidity) {
            return
        }

        // If liquidity is unavailable, set buy data value to 0
        // BUT only if this is not a Feather order with a quote already
        if (
            !liquidityAvailable &&
            !isWrapOnly(sellData, buyData) &&
            !isUnWrapOnly(sellData, buyData) &&
            !isFeatherOrder // Use the context's isFeatherOrder value instead of sellData.isFeatherOrder
        ) {
            // Only update if the value isn't already 0
            if (buyData.value !== BigInt(0)) {
                setBuyData(prev => ({ ...prev, value: BigInt(0) }))
            }
        }
    }, [isCheckingLiquidity, liquidityAvailable, sellData, buyData, isFeatherOrder])

    useEffect(() => {
        if (mainWalletAddress && sellData.token && buyData.token) {
            invalidateQuoteAndRefetch({
                walletAddress: mainWalletAddress.toString() || '',
                srcChain: getChainNameFromChainId(sellData.chainId),
                dstChain: getChainNameFromChainId(buyData.chainId),
                srcAssetAddress: sellData.token.address,
                dstAssetAddress: buyData.token.address,
                srcAmount: sellData.value,
                targetAddress: destinationAddress || '',
            })
        } else {
            logger.warn('Skipping quote refetch due to missing wallet address or token data')
        }
    }, [mainWalletAddress])

    useEffect(() => {
        if (mainWalletAddress && sellData.token && buyData.token) {
            invalidateQuoteAndRefetch({
                walletAddress: mainWalletAddress.toString() || '',
                srcChain: getChainNameFromChainId(sellData.chainId),
                dstChain: getChainNameFromChainId(buyData.chainId),
                srcAssetAddress: sellData.token.address,
                dstAssetAddress: buyData.token.address,
                srcAmount: sellData.value,
                targetAddress: destinationAddress || '',
            })
        } else {
            logger.warn('Skipping quote refetch due to missing wallet address or token data')
        }
    }, [destinationAddress])

    useEffect(() => {
        if (sellData?.chainId) {
            setWalletButtonDisplayChainId(sellData.chainId)
        }
    }, [sellData?.chainId, setWalletButtonDisplayChainId])

    useEffect(() => {
        // walletSelectedSellToken only changes when someone clicks an asset in the wallet drawer.
        if (walletSelectedSellToken) {
            // updateSellData internally handles changing URL and updating localStorage
            updateSellData({ token: walletSelectedSellToken, chainId: walletSelectedSellToken.chainId })
        }
    }, [walletSelectedSellToken])

    // Track if tokens have been initialized from localStorage/URL/defaults
    const tokensInitializedRef = useRef(false)

    // Track if user has navigated away from and back to the swap page
    const previousPathRef = useRef(location.pathname)
    const isReturningToSwapPageRef = useRef(false)

    // Track if tokens list was previously empty but is now loaded
    const previousTokensEmptyRef = useRef(true)

    // Track when a user has explicitly selected tokens
    const userExplicitlySelectedTokensRef = useRef({
        sell: false,
        buy: false,
    })

    // Track when the page path changes to detect return to swap page
    useEffect(() => {
        // If we've navigated away from swap page before...
        if (previousPathRef.current !== PATH.SWAP && location.pathname === PATH.SWAP) {
            // We're returning to the swap page
            logger.info('[buyselldefault] Returning to swap page, flagging for token restoration')
            isReturningToSwapPageRef.current = true
        } else if (previousPathRef.current === PATH.SWAP && location.pathname !== PATH.SWAP) {
            // We're navigating away from swap page
            logger.info('[buyselldefault] Navigating away from swap page')
        }

        // Update previous path reference
        previousPathRef.current = location.pathname
    }, [location.pathname])

    /**
     * Reads tokens from the URL first; if absent or invalid, tries localStorage;
     * then finally falls back to default tokens.
     */
    useEffect(() => {
        // Only run when tokens are loaded and have content
        if (tokensLoading || tokens.length === 0) {
            logger.info('[buyselldefault] Tokens not yet loaded, deferring token initialization')
            previousTokensEmptyRef.current = true
            return
        }

        // Check if tokens were previously empty but now loaded
        const tokensJustLoaded = previousTokensEmptyRef.current && tokens.length > 0
        previousTokensEmptyRef.current = false

        // Log tokens available count for debugging
        logger.info(`[buyselldefault] Tokens loaded (${tokens.length} available)`, {
            tokensJustLoaded,
            tokensInitialized: tokensInitializedRef.current,
            isReturningToSwapPage: isReturningToSwapPageRef.current,
            userExplicitlySelectedSell: userExplicitlySelectedTokensRef.current.sell,
            userExplicitlySelectedBuy: userExplicitlySelectedTokensRef.current.buy,
        })

        // Reset initialization flag if tokens were just loaded to force token initialization
        // But ONLY if we're not returning to the swap page
        if (tokensJustLoaded && !isReturningToSwapPageRef.current) {
            logger.info('[buyselldefault] Tokens just loaded, resetting initialization flag to force processing')
            tokensInitializedRef.current = false
        }

        // Check if URL has token parameters - if we have URL params, always process them
        const params = getURLParameters()
        const hasUrlParams = (params.sellChain && params.sellToken) || (params.buyChain && params.buyToken)

        logger.info('[buyselldefault] URL parameters:', params)

        // Check if URL parameters match default tokens
        const defaultTokens = getDefaultTokens()
        const sellParamIsDefault =
            params.sellChain &&
            params.sellToken &&
            Number(params.sellChain) === defaultTokens.sellToken.chainId &&
            params.sellToken.toLowerCase() === defaultTokens.sellToken.address.toLowerCase()

        const buyParamIsDefault =
            params.buyChain &&
            params.buyToken &&
            Number(params.buyChain) === defaultTokens.buyToken.chainId &&
            params.buyToken.toLowerCase() === defaultTokens.buyToken.address.toLowerCase()

        // If both params match defaults, treat as if no URL params exist for priority logic
        const hasNonDefaultUrlParams = hasUrlParams && !(sellParamIsDefault && buyParamIsDefault)

        // Log the initial data for better analysis
        logger.info('[buyselldefault] Current token state before processing:', {
            currentSellToken: sellData.token?.symbol,
            currentSellChain: sellData.chainId,
            currentBuyToken: buyData.token?.symbol,
            currentBuyChain: buyData.chainId,
            hasUrlParams,
            hasNonDefaultUrlParams,
            sellParamIsDefault,
            buyParamIsDefault,
            tokensInitialized: tokensInitializedRef.current,
            isReturningToSwapPage: isReturningToSwapPageRef.current,
            path: location.pathname,
        })

        // Check localStorage for better analysis
        const localStorageSellToken = localStorage.getItem(LOCAL_STORAGE_SELL_TOKEN_KEY)
        const localStorageBuyToken = localStorage.getItem(LOCAL_STORAGE_BUY_TOKEN_KEY)
        logger.info('[buyselldefault] LocalStorage data:', {
            sellTokenExists: !!localStorageSellToken,
            buyTokenExists: !!localStorageBuyToken,
            sellTokenData: localStorageSellToken ? JSON.parse(localStorageSellToken) : null,
            buyTokenData: localStorageBuyToken ? JSON.parse(localStorageBuyToken) : null,
        })

        // When we're returning to the swap page, we want to preserve user's token selections
        // regardless of URL parameters (which may be stale from previous visits)
        if (location.pathname === PATH.SWAP && isReturningToSwapPageRef.current) {
            logger.info('[buyselldefault] Returning to swap page - preserving current token selections')

            // Clear URL parameters to prevent them from overriding token selections
            if (hasUrlParams) {
                logger.info('[buyselldefault] Clearing URL parameters to prevent override')
                // Only update URL if not in embed mode
                const isEmbedMode = location.pathname.includes('/embed')
                if (!isEmbedMode) {
                    // Clear the URL parameters but keep the path
                    replaceState(null, '', location.pathname)
                }
            }

            // Reset the returning flag now that we've handled it
            isReturningToSwapPageRef.current = false
            // Mark tokens as initialized to prevent further reinitialization
            tokensInitializedRef.current = true
            return // Skip the rest of token initialization
        }

        // Only run initialization if we haven't initialized tokens yet or if we explicitly have non-default URL params
        // AND we're not returning to the swap page (to avoid overriding user selections)
        if (
            location.pathname === PATH.SWAP &&
            ((hasNonDefaultUrlParams &&
                !userExplicitlySelectedTokensRef.current.sell &&
                !userExplicitlySelectedTokensRef.current.buy) ||
                !tokensInitializedRef.current) &&
            !isReturningToSwapPageRef.current
        ) {
            logger.info('[buyselldefault] Initializing tokens from URL/localStorage/defaults', {
                hasUrlParams,
                hasNonDefaultUrlParams,
                sellParamIsDefault,
                buyParamIsDefault,
            })

            // Reset URL processing flag when URL changes with non-default params
            if (hasNonDefaultUrlParams) {
                logger.info('Non-default URL parameters detected, processing them', params)
            }

            // SELL token
            let sellFoundToken: IExchangeData['token'] | null = null

            // Try localStorage first when there are no non-default URL params
            const localStoredSellToken = loadTokenFromLocalStorage(LOCAL_STORAGE_SELL_TOKEN_KEY, tokens)
            if (!hasNonDefaultUrlParams && localStoredSellToken) {
                sellFoundToken = localStoredSellToken
                logger.info(
                    '[buyselldefault] sellData loaded directly from localStorage first:',
                    localStoredSellToken.symbol
                )
            }

            // If not found in localStorage and we have non-default URL params, use those
            if (
                !sellFoundToken &&
                hasNonDefaultUrlParams &&
                params.sellChain &&
                params.sellToken &&
                !sellParamIsDefault
            ) {
                try {
                    const foundToken = tokens.find(
                        o =>
                            o.chainId === Number(params.sellChain) &&
                            o.address.toLowerCase() === params.sellToken.toLowerCase()
                    )
                    if (foundToken) {
                        sellFoundToken = foundToken
                        logger.info('[buyselldefault] sellData updated from URL params:', foundToken.symbol)
                    } else {
                        logger.warn(
                            `[buyselldefault] Cannot find token with chainId=${Number(params.sellChain)} and address=${params.sellToken} in token list`
                        )
                    }
                } catch (e) {
                    logger.error('[buyselldefault] Cannot parse sellData from URL parameters:', e)
                }
            }

            // If still not found, try current token (if it's not default)
            if (!sellFoundToken) {
                if (sellData.token && sellData.token !== getDefaultTokens().sellToken) {
                    sellFoundToken = sellData.token
                    logger.info('[buyselldefault] Using current non-default sell token:', sellFoundToken.symbol)
                }
            }

            // Try localStorage again if we still don't have a token and we didn't try it already
            if (!sellFoundToken && !localStoredSellToken) {
                const retryLocalStoredSellToken = loadTokenFromLocalStorage(LOCAL_STORAGE_SELL_TOKEN_KEY, tokens)
                if (retryLocalStoredSellToken) {
                    sellFoundToken = retryLocalStoredSellToken
                    logger.info(
                        '[buyselldefault] sellData updated from localStorage (retry):',
                        retryLocalStoredSellToken.symbol
                    )
                } else {
                    logger.info('[buyselldefault] No valid sellData found in localStorage.')
                }
            }

            // Only use default if we have no other token
            if (!sellFoundToken) {
                sellFoundToken = getDefaultTokens().sellToken
                logger.info('[buyselldefault] sellData defaulted to:', getDefaultTokens().sellToken.symbol)
            }

            // BUY token
            let buyFoundToken: IExchangeData['token'] | null = null

            // Try localStorage first when there are no non-default URL params
            const localStoredBuyToken = loadTokenFromLocalStorage(LOCAL_STORAGE_BUY_TOKEN_KEY, tokens)
            if (!hasNonDefaultUrlParams && localStoredBuyToken) {
                buyFoundToken = localStoredBuyToken
                logger.info(
                    '[buyselldefault] buyData loaded directly from localStorage first:',
                    localStoredBuyToken.symbol
                )
            }

            // If not found in localStorage and we have non-default URL params, use those
            if (!buyFoundToken && hasNonDefaultUrlParams && params.buyChain && params.buyToken && !buyParamIsDefault) {
                try {
                    const foundToken = tokens.find(
                        o =>
                            o.chainId === Number(params.buyChain) &&
                            o.address.toLowerCase() === params.buyToken.toLowerCase()
                    )
                    if (foundToken) {
                        buyFoundToken = foundToken
                        logger.info('[buyselldefault] buyData updated from URL params:', foundToken.symbol)
                    } else {
                        logger.warn(
                            `[buyselldefault] Cannot find token with chainId=${Number(params.buyChain)} and address=${params.buyToken} in token list`
                        )
                    }
                } catch (e) {
                    logger.error('[buyselldefault] Cannot parse buyData from URL parameters:', e)
                }
            }

            // If still not found, try current token (if it's not default)
            if (!buyFoundToken) {
                if (buyData.token && buyData.token !== getDefaultTokens().buyToken) {
                    buyFoundToken = buyData.token
                    logger.info('[buyselldefault] Using current non-default buy token:', buyFoundToken.symbol)
                }
            }

            // Try localStorage again if we still don't have a token and we didn't try it already
            if (!buyFoundToken && !localStoredBuyToken) {
                const retryLocalStoredBuyToken = loadTokenFromLocalStorage(LOCAL_STORAGE_BUY_TOKEN_KEY, tokens)
                if (retryLocalStoredBuyToken) {
                    buyFoundToken = retryLocalStoredBuyToken
                    logger.info(
                        '[buyselldefault] buyData updated from localStorage (retry):',
                        retryLocalStoredBuyToken.symbol
                    )
                } else {
                    logger.info('[buyselldefault] No valid buyData found in localStorage.')
                }
            }

            // Only use default if we have no other token
            if (!buyFoundToken) {
                buyFoundToken = getDefaultTokens().buyToken
                logger.info('[buyselldefault] buyData defaulted to:', getDefaultTokens().buyToken.symbol)
            }

            // Check if buy token is the same as sell token (same token on same chain)
            if (
                sellFoundToken &&
                buyFoundToken &&
                sellFoundToken.chainId === buyFoundToken.chainId &&
                sellFoundToken.address.toLowerCase() === buyFoundToken.address.toLowerCase()
            ) {
                logger.warn('Buy token is the same as sell token, finding alternative')

                // Try to find a different token on the same chain first
                const differentTokenOnSameChain = tokens.find(
                    t =>
                        t.chainId === buyFoundToken!.chainId &&
                        t.address.toLowerCase() !== sellFoundToken!.address.toLowerCase()
                )

                if (differentTokenOnSameChain) {
                    buyFoundToken = differentTokenOnSameChain
                    logger.info('[buyselldefault] Using different token on same chain:', buyFoundToken.symbol)
                } else {
                    // Fall back to default buy token if no other token found on same chain
                    buyFoundToken = getDefaultTokens().buyToken
                    logger.info(
                        '[buyselldefault] No alternative token found on chain, using default:',
                        getDefaultTokens().buyToken.symbol
                    )
                }
            }

            // Update the tokens if they were found
            if (sellFoundToken && buyFoundToken) {
                updateBothData(
                    {
                        token: sellFoundToken,
                        chainId: sellFoundToken.chainId,
                    },
                    {
                        token: buyFoundToken,
                        chainId: buyFoundToken.chainId,
                    }
                )
            } else {
                // In case of any errors, use static defaults
                logger.warn('Using static defaults due to token initialization issues')
                updateBothData(
                    {
                        token: DEFAULT_SELL_TOKEN,
                        chainId: DEFAULT_SELL_TOKEN.chainId,
                    },
                    {
                        token: DEFAULT_BUY_TOKEN,
                        chainId: DEFAULT_BUY_TOKEN.chainId,
                    }
                )
            }

            // Mark tokens as initialized to prevent overriding user selections
            // Only set initialized if not processing non-default URL params
            if (!hasNonDefaultUrlParams) {
                tokensInitializedRef.current = true
                logger.info('[buyselldefault] Marked tokens as initialized')
            }
            // Log the final token selection for analysis
            logger.info('[buyselldefault] Token initialization complete', {
                finalSellToken: sellFoundToken?.symbol,
                finalSellChain: sellFoundToken?.chainId,
                finalBuyToken: buyFoundToken?.symbol,
                finalBuyChain: buyFoundToken?.chainId,
                sourceOfSellToken:
                    sellFoundToken === getDefaultTokens().sellToken
                        ? 'default'
                        : params.sellChain && params.sellToken && !sellParamIsDefault
                          ? 'URL'
                          : sellData.token && sellData.token !== getDefaultTokens().sellToken
                            ? 'current'
                            : 'localStorage',
                sourceOfBuyToken:
                    buyFoundToken === getDefaultTokens().buyToken
                        ? 'default'
                        : params.buyChain && params.buyToken && !buyParamIsDefault
                          ? 'URL'
                          : buyData.token && buyData.token !== getDefaultTokens().buyToken
                            ? 'current'
                            : 'localStorage',
            })
        }

        // IMPORTANT: DO NOT include sellData.token or buyData.token in the dependencies
        // as this creates a circular dependency and causes multiple initializations
        // Using a stable reference for tokens to prevent unnecessary re-renders
    }, [location.pathname, tokensLoading, !!tokens.length])

    const updateSellData = (updates: Partial<IExchangeData>) => {
        setIsReadyForQuote(false)

        let stateUpdate: any = {}

        // If token is being updated, mark as user-selected
        if (updates.token) {
            setUserSelectedSellToken(true)
            userExplicitlySelectedTokensRef.current.sell = true
            logger.debug('[buyselldefault] User explicitly selected sell token:', updates.token.symbol)

            if (
                updates.token.address.toLowerCase() !== sellData.token.address.toLowerCase() ||
                updates.token.chainId !== sellData.token.chainId
            ) {
                stateUpdate = { ...stateUpdate, sellChain: updates.token.chainId, sellToken: updates.token.address }
            }
        }

        // Only update URL if not in embed mode
        const isEmbedMode = location.pathname.includes('/embed')
        if (!isEmbedMode) {
            const urlString = getUpdatedURLString(stateUpdate)
            replaceState(null, '', urlString)
        } else {
            logger.debug('In embed mode - not updating URL parameters for sell token change', {
                token: updates.token?.symbol,
                chainId: updates.token?.chainId,
            })
        }

        setSellData(prevSellData => ({
            ...prevSellData,
            ...updates,
        }))

        setTimeout(() => {
            setIsReadyForQuote(true)
        }, 10)
    }

    const updateBuyData = (updates: Partial<IExchangeData>) => {
        setIsReadyForQuote(false)

        let stateUpdate: any = {}

        // If token is being updated, mark as user-selected
        if (updates.token) {
            setUserSelectedBuyToken(true)
            userExplicitlySelectedTokensRef.current.buy = true
            logger.debug('[buyselldefault] User explicitly selected buy token:', updates.token.symbol)

            if (
                updates.token.address.toLowerCase() !== buyData.token.address.toLowerCase() ||
                updates.token.chainId !== buyData.token.chainId
            ) {
                stateUpdate = { ...stateUpdate, buyChain: updates.token.chainId, buyToken: updates.token.address }
            }
        }

        // Only update URL if not in embed mode
        const isEmbedMode = location.pathname.includes('/embed')
        if (!isEmbedMode) {
            const urlString = getUpdatedURLString(stateUpdate)
            replaceState(null, '', urlString)
        } else {
            logger.debug('In embed mode - not updating URL parameters for buy token change', {
                token: updates.token?.symbol,
                chainId: updates.token?.chainId,
            })
        }

        setBuyData(prevBuyData => ({
            ...prevBuyData,
            ...updates,
        }))

        setTimeout(() => {
            setIsReadyForQuote(true)
        }, 10)
    }

    const updateBothData = (sellUpdates: Partial<IExchangeData>, buyUpdates: Partial<IExchangeData>) => {
        setIsReadyForQuote(false)

        let stateUpdate: any = {}

        // Process sell updates
        if (sellUpdates.token) {
            // During initialization, don't mark as user-selected unless it's explicitly mentioned
            if (!tokensInitializedRef.current) {
                logger.debug('[buyselldefault] Initial load of sell token:', sellUpdates.token.symbol)
            } else {
                setUserSelectedSellToken(true)
                userExplicitlySelectedTokensRef.current.sell = true
                logger.debug(
                    '[buyselldefault] User explicitly selected sell token in updateBothData:',
                    sellUpdates.token.symbol
                )
            }

            if (
                sellUpdates.token.address.toLowerCase() !== sellData.token.address.toLowerCase() ||
                sellUpdates.token.chainId !== sellData.token.chainId
            ) {
                stateUpdate = {
                    ...stateUpdate,
                    sellChain: sellUpdates.token.chainId,
                    sellToken: sellUpdates.token.address,
                }
            }
        }

        // Process buy updates
        if (buyUpdates.token) {
            // During initialization, don't mark as user-selected unless it's explicitly mentioned
            if (!tokensInitializedRef.current) {
                logger.debug('[buyselldefault] Initial load of buy token:', buyUpdates.token.symbol)
            } else {
                setUserSelectedBuyToken(true)
                userExplicitlySelectedTokensRef.current.buy = true
                logger.debug(
                    '[buyselldefault] User explicitly selected buy token in updateBothData:',
                    buyUpdates.token.symbol
                )
            }

            if (
                buyUpdates.token.address.toLowerCase() !== buyData.token.address.toLowerCase() ||
                buyUpdates.token.chainId !== buyData.token.chainId
            ) {
                stateUpdate = {
                    ...stateUpdate,
                    buyChain: buyUpdates.token.chainId,
                    buyToken: buyUpdates.token.address,
                }
            }
        }

        // Only update URL if not in embed mode
        const isEmbedMode = location.pathname.includes('/embed')
        if (!isEmbedMode) {
            const urlString = getUpdatedURLString(stateUpdate)
            replaceState(null, '', urlString)
        } else {
            logger.debug('In embed mode - not updating URL parameters for token changes', {
                sellToken: sellUpdates.token?.symbol,
                sellChainId: sellUpdates.token?.chainId,
                buyToken: buyUpdates.token?.symbol,
                buyChainId: buyUpdates.token?.chainId,
            })
        }

        setSellData(prev => ({ ...prev, ...sellUpdates }))

        setBuyData(prev => ({ ...prev, ...buyUpdates }))

        setTimeout(() => {
            setIsReadyForQuote(true)
        }, 10)
    }

    useEffect(() => {
        logger.info('sellData in provider:', sellData)
    }, [sellData])

    useEffect(() => {
        logger.info('buyData in provider:', buyData)
    }, [buyData])

    // Log gas estimates data whenever it changes
    useEffect(() => {
        if (gasEstimatesData) {
            logger.info('Gas estimates data received:', gasEstimatesData)
        }
    }, [gasEstimatesData])

    // FE-523, FE-643
    useEffect(() => {
        if (sellData.token && buyData.token && (isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData))) {
            const reason = isWrapOnly(sellData, buyData) ? 'wrap only' : 'unwrap only'
            logger.warn(`[ExchangeDataContext] Quote disabled: ${reason} operation`)
            // Disable quote loading and the quote API call for wrap/unwrap operations
            setIsQuoteEnabled(false)
        } else {
            // Re-enable quotes for normal token exchanges
            setIsQuoteEnabled(true)
        }
    }, [sellData.token, buyData.token, setIsQuoteEnabled])

    const quoteHookEnabled = isQuoteEnabled && !isCheckingLiquidity && liquidityAvailable
    useEffect(() => {
        if (isQuoteEnabled && !quoteHookEnabled) {
            const reasons: string[] = []
            if (!liquidityAvailable) reasons.push('liquidity unavailable')
            if (isCheckingLiquidity) reasons.push('checking liquidity')
            logger.warn(`[ExchangeDataContext] Quote disabled due to: ${reasons.join(', ')}`)
        }
    }, [isQuoteEnabled, liquidityAvailable, isCheckingLiquidity, quoteHookEnabled])

    /**
     * Whenever sellData.token changes, store it in localStorage if:
     * 1. It's not the default sell token, OR
     * 2. It was explicitly selected by the user (even if it's a default token)
     */
    useEffect(() => {
        if (sellData?.token) {
            const defaultTokens = getDefaultTokens()
            const isDefaultSellToken =
                sellData.token.chainId === defaultTokens.sellToken.chainId &&
                sellData.token.address.toLowerCase() === defaultTokens.sellToken.address.toLowerCase()

            if (!isDefaultSellToken || userSelectedSellToken) {
                if (isDefaultSellToken && userSelectedSellToken) {
                    logger.debug(
                        '[buyselldefault] Storing user-selected default sell token in localStorage:',
                        sellData.token.symbol
                    )
                } else {
                    logger.debug(
                        '[buyselldefault] Storing non-default sell token in localStorage:',
                        sellData.token.symbol
                    )
                }
                localStorage.setItem(LOCAL_STORAGE_SELL_TOKEN_KEY, JSON.stringify(sellData.token))
            } else {
                logger.debug('[buyselldefault] Not storing auto-loaded default sell token in localStorage')
            }
        }
    }, [sellData.token, userSelectedSellToken])

    /**
     * Whenever buyData.token changes, store it in localStorage if:
     * 1. It's not the default buy token, OR
     * 2. It was explicitly selected by the user (even if it's a default token)
     */
    useEffect(() => {
        if (buyData?.token) {
            const defaultTokens = getDefaultTokens()
            const isDefaultBuyToken =
                buyData.token.chainId === defaultTokens.buyToken.chainId &&
                buyData.token.address.toLowerCase() === defaultTokens.buyToken.address.toLowerCase()

            if (!isDefaultBuyToken || userSelectedBuyToken) {
                if (isDefaultBuyToken && userSelectedBuyToken) {
                    logger.debug(
                        '[buyselldefault] Storing user-selected default buy token in localStorage:',
                        buyData.token.symbol
                    )
                } else {
                    logger.debug(
                        '[buyselldefault] Storing non-default buy token in localStorage:',
                        buyData.token.symbol
                    )
                }
                localStorage.setItem(LOCAL_STORAGE_BUY_TOKEN_KEY, JSON.stringify(buyData.token))
            } else {
                logger.debug('[buyselldefault] Not storing auto-loaded default buy token in localStorage')
            }
        }
    }, [buyData.token, userSelectedBuyToken])

    // Jupiter route modal state
    const [isJupiterRouteModalOpen, setIsJupiterRouteModalOpen] = useState(false)

    const openJupiterRouteModal = () => {
        setIsJupiterRouteModalOpen(true)
    }

    const closeJupiterRouteModal = () => {
        setIsJupiterRouteModalOpen(false)
    }

    const contextValue = {
        sellData,
        buyData,
        destinationAddress,
        quoteResponse,
        isQuoteLoading,
        refetchQuote,
        invalidateQuoteAndRefetch,
        forceCancelQuote,
        mainWalletAddress,
        updateSellData,
        updateBuyData,
        updateBothData,
        setDestinationAddress,
        transactionCapStatus,
        isLoadingTransactionCap,
        transactionCapError,
        isFeatherOrder,
        liquidityAvailable,
        isCheckingLiquidity,
        liquidityError,
        milesStatus,
        featherQuote,
        featherQuoteError,
        isJupiterRouteModalOpen,
        openJupiterRouteModal,
        closeJupiterRouteModal,
    }

    return <ExchangeDataContext.Provider value={contextValue}>{children}</ExchangeDataContext.Provider>
}
